import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared Modal component (design-system Phase 2), used across the app.
 *
 * Props:
 *  - open (bool), onClose (fn)
 *  - title, subtitle, children, footer
 *  - closeOnBackdropClick (default true), closeOnEscape (default true)
 *  - preventClose: when true, overrides the above and also disables the header
 *    close (×) button — for in-flight async operations (e.g. a bulk action)
 *    where the user shouldn't be able to dismiss the dialog mid-request
 *  - size: 'sm' | 'md' | 'lg'
 *
 * Behavior: locks background scroll while open, traps Tab focus within the
 * dialog, restores focus to the element that opened it on close, and closes
 * on Escape / backdrop click (both configurable, since the audit found
 * existing hand-rolled modals differ on backdrop-click behavior).
 */
export default function Modal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  preventClose = false,
  size = 'md',
  labelledBy,
  className = '',
}) {
  const panelRef = useRef(null);
  const previousActiveElementRef = useRef(null);
  const titleId = labelledBy || 'ds-modal-title';
  // The latest handlers, read when a key is pressed. Keeping them out of the
  // effect below means a parent re-render (e.g. typing in a field, which
  // passes a new onClose) doesn't re-run it and move focus away.
  const latest = useRef({ onClose, closeOnEscape, preventClose });
  latest.current = { onClose, closeOnEscape, preventClose };

  useEffect(() => {
    if (!open) return undefined;

    previousActiveElementRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Start in the first field of the body, else the first control.
    const panel = panelRef.current;
    const firstField = panel?.querySelector(`.ds-modal-body :is(${FOCUSABLE_SELECTOR})`);
    const focusables = panel ? panel.querySelectorAll(FOCUSABLE_SELECTOR) : [];
    (firstField || focusables[0] || panel)?.focus();

    function handleKeyDown(event) {
      const { onClose: close, closeOnEscape: escCloses, preventClose: locked } = latest.current;
      if (event.key === 'Escape' && escCloses && !locked) {
        event.stopPropagation();
        close?.();
        return;
      }
      if (event.key === 'Tab' && panel) {
        const nodes = panel.querySelectorAll(FOCUSABLE_SELECTOR);
        if (nodes.length === 0) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      previousActiveElementRef.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  function handleBackdropMouseDown(event) {
    if (closeOnBackdropClick && !preventClose && event.target === event.currentTarget) {
      onClose?.();
    }
  }

  return createPortal(
    <div className="ds-modal-backdrop" onMouseDown={handleBackdropMouseDown}>
      <div
        className={`ds-modal-panel ds-modal-${size} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        ref={panelRef}
        tabIndex={-1}
      >
        {title && (
          <div className="ds-modal-header">
            <div className="ds-modal-heading">
              <h2 className="ds-modal-title" id={titleId}>
                {title}
              </h2>
              {subtitle && <p className="ds-modal-subtitle">{subtitle}</p>}
            </div>
            <button
              type="button"
              className="ds-modal-close"
              aria-label="Close dialog"
              onClick={() => onClose?.()}
              disabled={preventClose}
            >
              &times;
            </button>
          </div>
        )}
        <div className="ds-modal-body">{children}</div>
        {footer && <div className="ds-modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
